// Tank Mix Calculator — pick the products going into one tank + the area
// covered + the tank size, get per-product quantities. Also surfaces basic
// compatibility warnings by category (which pairings to jar-test first).
//
// Pure client-side; no persistence. When a mix is ready to execute the
// user clicks "Log each as application" which links out to the Log
// Application form with pre-filled product IDs (future slice — for now
// just prints the shopping list).

import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { Product, ProductCategory } from '@greeniq/shared';
import { ApiError } from '../api/client.js';
import { listProducts } from '../api/chemical.js';

interface MixLine {
  productId: number;
  ratePer1000: string;
  rateUnit: 'fl oz' | 'oz' | 'lb' | 'g';
}

type CategoryPairKey = string;
interface Advisory {
  level: 'warn' | 'critical';
  pair: string;
  message: string;
}

// Category-level compatibility heuristics. This is NOT label-level chemistry
// — it's the "always jar-test first" signals every superintendent knows.
const PAIR_ADVISORIES: Record<CategoryPairKey, Advisory> = {
  'fungicide|fertilizer': {
    level: 'warn',
    pair: 'Fungicide + fertilizer',
    message: 'Copper / iron-based fungicides + acidic fertilizers can form unstable complexes. Jar-test first.',
  },
  'herbicide|fertilizer': {
    level: 'warn',
    pair: 'Herbicide + fertilizer',
    message: '2,4-D and other phenoxys can bind with Ca/Mg in liquid fert. Use AMS if combining.',
  },
  'wetting_agent|fertilizer': {
    level: 'warn',
    pair: 'Wetting agent + liquid fert',
    message: 'Some polymer surfactants precipitate in high-Ca fert stocks. Agitate continuously.',
  },
  'fungicide|herbicide': {
    level: 'warn',
    pair: 'Fungicide + herbicide',
    message: 'Acidifying herbicides can destabilize SDHI fungicides. Add herbicide last.',
  },
  'insecticide|fungicide': {
    level: 'warn',
    pair: 'Insecticide + fungicide',
    message: 'Pyrethroid + chlorothalonil is generally OK but monitor for flocculation in hard water.',
  },
};

export function TankMixCalculatorPage() {
  const { courseSlug } = useParams();
  const [products, setProducts] = useState<Product[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [lines, setLines] = useState<MixLine[]>([]);
  const [areaSqft, setAreaSqft] = useState('43560'); // default 1 acre
  const [tankGal, setTankGal] = useState('200');
  const [waterRatePer1000, setWaterRatePer1000] = useState('2.0'); // gal water / 1000 sqft

  useEffect(() => {
    if (!courseSlug) return;
    (async () => {
      try {
        const r = await listProducts(courseSlug, { limit: 500 });
        setProducts(r.products);
      } catch (err) {
        setError(err instanceof ApiError ? err.message : String(err));
      }
    })();
  }, [courseSlug]);

  const productById = useMemo(() => new Map(products.map((p) => [p.id, p])), [products]);

  function addLine() {
    const first = products[0];
    if (!first) return;
    setLines((prev) => [...prev, { productId: first.id, ratePer1000: '', rateUnit: 'fl oz' }]);
  }
  function removeLine(idx: number) {
    setLines((prev) => prev.filter((_, i) => i !== idx));
  }
  function updateLine(idx: number, patch: Partial<MixLine>) {
    setLines((prev) => prev.map((line, i) => (i === idx ? { ...line, ...patch } : line)));
  }

  const area = Number(areaSqft);
  const totalWaterGal = Number.isFinite(area) && area > 0
    ? (area / 1000) * Number(waterRatePer1000)
    : 0;

  const advisories: Advisory[] = useMemo(() => {
    const cats = new Set<ProductCategory>();
    for (const l of lines) {
      const p = productById.get(l.productId);
      if (p) cats.add(p.category);
    }
    const catArr = Array.from(cats).sort();
    const out: Advisory[] = [];
    for (let i = 0; i < catArr.length; i++) {
      for (let j = i + 1; j < catArr.length; j++) {
        const key = `${catArr[i]}|${catArr[j]}`;
        const advisory = PAIR_ADVISORIES[key];
        if (advisory) out.push(advisory);
      }
    }
    return out;
  }, [lines, productById]);

  if (error) return <div className="p-8 text-[color:var(--color-error)]">{error}</div>;

  return (
    <div className="p-6 md:p-10 max-w-5xl">
      <Link to={`/app/${courseSlug}/chemical`} className="text-sm text-greeniq-400 hover:underline">
        ← Chemical &amp; Inventory
      </Link>

      <div className="mt-3">
        <p className="uppercase tracking-wider text-xs text-greeniq-400 font-semibold">Tank Mix</p>
        <h1 className="mt-1 text-3xl md:text-4xl font-display font-bold text-fg">Tank Mix Calculator</h1>
        <p className="mt-1 text-fg-muted">
          Plan an application: pick products + rates, see total quantities + compatibility advisories. Always jar-test mixes the first time.
        </p>
      </div>

      <div className="mt-8 grid md:grid-cols-3 gap-4">
        <label className="block">
          <span className="text-xs uppercase tracking-wider text-fg-muted">Area (sqft)</span>
          <input
            type="number"
            value={areaSqft}
            onChange={(e) => setAreaSqft(e.target.value)}
            className="mt-1 w-full border border-border rounded-md py-2 px-2"
          />
          <span className="text-xs text-fg-muted">= {(area / 43560).toFixed(2)} acres</span>
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-wider text-fg-muted">Water rate (gal / 1000 sqft)</span>
          <input
            type="number"
            step="0.1"
            value={waterRatePer1000}
            onChange={(e) => setWaterRatePer1000(e.target.value)}
            className="mt-1 w-full border border-border rounded-md py-2 px-2"
          />
          <span className="text-xs text-fg-muted">Total: {totalWaterGal.toFixed(1)} gal water</span>
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-wider text-fg-muted">Tank size (gal)</span>
          <input
            type="number"
            value={tankGal}
            onChange={(e) => setTankGal(e.target.value)}
            className="mt-1 w-full border border-border rounded-md py-2 px-2"
          />
          <span className="text-xs text-fg-muted">
            {totalWaterGal > 0 && Number(tankGal) > 0
              ? `${Math.ceil(totalWaterGal / Number(tankGal))} tank${Math.ceil(totalWaterGal / Number(tankGal)) === 1 ? '' : 's'}`
              : ''}
          </span>
        </label>
      </div>

      <div className="mt-8">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm uppercase tracking-wider text-fg-muted font-semibold">Products in this tank</h2>
          <button
            onClick={addLine}
            disabled={products.length === 0}
            className="px-3 py-1.5 text-xs rounded-md bg-bg-hover text-greeniq-300 font-semibold hover:bg-bg-elevated disabled:opacity-50"
          >
            + Add product
          </button>
        </div>

        {lines.length === 0 ? (
          <div className="p-6 text-center text-sm text-fg-muted bg-bg-card rounded-lg border border-dashed border-border">
            No products added yet.
          </div>
        ) : (
          <div className="overflow-auto bg-bg-card rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-border bg-bg-elevated">
                  <th className="px-3 py-2 font-semibold text-fg-muted">Product</th>
                  <th className="px-3 py-2 font-semibold text-fg-muted text-right">Rate / 1000</th>
                  <th className="px-3 py-2 font-semibold text-fg-muted text-right">Total for area</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {lines.map((line, idx) => {
                  const p = productById.get(line.productId);
                  const rate = Number(line.ratePer1000);
                  const total = Number.isFinite(rate) && Number.isFinite(area)
                    ? (rate * area) / 1000
                    : 0;
                  return (
                    <tr key={idx} className="border-b border-border last:border-b-0">
                      <td className="px-3 py-2">
                        <select
                          value={line.productId}
                          onChange={(e) => updateLine(idx, { productId: Number(e.target.value) })}
                          className="w-full border border-border rounded-md py-1.5 px-2"
                        >
                          {products.map((prod) => (
                            <option key={prod.id} value={prod.id}>
                              {prod.name} · {prod.category.replace(/_/g, ' ')}
                            </option>
                          ))}
                        </select>
                        {p?.typicalRatePer1000sqft && (
                          <div className="text-[10px] text-fg-muted mt-1">
                            Label: {p.typicalRatePer1000sqft}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex gap-1 justify-end">
                          <input
                            type="number"
                            step="0.001"
                            value={line.ratePer1000}
                            onChange={(e) => updateLine(idx, { ratePer1000: e.target.value })}
                            className="w-20 border border-border rounded-md py-1.5 px-2 text-right"
                            placeholder="0"
                          />
                          <select
                            value={line.rateUnit}
                            onChange={(e) => updateLine(idx, { rateUnit: e.target.value as MixLine['rateUnit'] })}
                            className="border border-border rounded-md py-1.5 px-1 text-xs"
                          >
                            <option value="fl oz">fl oz</option>
                            <option value="oz">oz</option>
                            <option value="lb">lb</option>
                            <option value="g">g</option>
                          </select>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap font-mono">
                        {total > 0 ? `${total.toFixed(total < 10 ? 2 : 1)} ${line.rateUnit}` : '—'}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button
                          onClick={() => removeLine(idx)}
                          className="text-fg-muted hover:text-[color:var(--color-error)] text-xs"
                          aria-label="Remove"
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {advisories.length > 0 && (
        <div className="mt-6 p-4 bg-warning-muted border border-[color:var(--color-warning)]/30 rounded-lg">
          <h3 className="text-xs uppercase tracking-wider text-[color:var(--color-warning)] font-semibold">
            Compatibility advisories
          </h3>
          <ul className="mt-2 space-y-2">
            {advisories.map((a, i) => (
              <li key={i} className="text-sm text-[color:var(--color-warning)]">
                <strong>{a.pair}:</strong> {a.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      <p className="mt-6 text-xs text-fg-muted">
        Calculations are per-label rate × area. This tool does not replace manufacturer jar-test protocols. Always consult the label and verify physical compatibility in a small batch first.
      </p>
    </div>
  );
}
